import { useCallback, useEffect, useRef } from "react";
import io, { Socket } from "socket.io-client";
import { dmnIdentity } from "../utils/dmnIdentity";
import { getEnvironmentProps } from "../utils/environment";

export type customMessageEvent<T = any> = {
  channel?: string;
  message: T;
};
type UseWebSocketOptions = {
  channel: string;
  onMessage: (e: customMessageEvent) => void;
  onDisconnect?: () => void;
};
export const useWebSocket = ({
  channel,
  onMessage,
  onDisconnect
}: UseWebSocketOptions) => {
  const sessionRef = useRef<Socket | null>(null);
  const channelRef = useRef<string>(channel);
  const previousChannelRef = useRef<string>("");

  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const onDisconnectRef = useRef(onDisconnect);
  useEffect(() => {
    onDisconnectRef.current = onDisconnect;
  }, [onDisconnect]);

  useEffect(() => {
    channelRef.current = channel;
  }, [channel]);

  const subscribe = useCallback(async () => {
    const socket = sessionRef.current;
    const ch = channelRef.current;
    if (!socket?.connected || ch === "") {
      previousChannelRef.current = "";
      return;
    }

    const token = await dmnIdentity.getAccessToken();

    socket.emit(
      "subscribeToChannel",
      { channel: ch, token },
      (response: any) => {
        switch (response.status) {
          case 200:
            previousChannelRef.current = ch;
            break;
          case 401:
            console.warn("websocket Unauthenticated");
            break;
          case 403:
            console.warn("websocket Unauthorised");
            break;
          default:
            console.warn(`websocket error. Status code: ${response.status}`);
        }
      }
    );
  }, []);

  const unSubscribe = useCallback(async () => {
    const socket = sessionRef.current;
    if (!socket || !previousChannelRef.current) return;

    const token = await dmnIdentity.getAccessToken();

    socket.emit("unsubscribeFromChannel", {
      channel: previousChannelRef.current,
      token
    });

    previousChannelRef.current = "";
  }, []);

  useEffect(() => {
    let cancelled = false;

    const connectSocket = async () => {
      const token = await dmnIdentity.getAccessToken();

      if (cancelled) return;

      const environmentVars = getEnvironmentProps(token);

      if (!environmentVars?.WEB_SOCKET_URL) {
        console.warn("No websocket to connect to");
        return;
      }

      try {
        sessionRef.current?.close();

        const websocket = io(environmentVars.WEB_SOCKET_URL, {
          query: { token },
          reconnectionAttempts: 3,
          secure: true,
          withCredentials: true,
          transports: ["websocket", "polling"]
        });

        sessionRef.current = websocket;

        websocket.on("connect", () => {
          subscribe();
        });

        websocket.on("channel_message", (event: customMessageEvent) => {
          onMessageRef.current?.(event);
        });

        websocket.on("disconnect", () => {
          onDisconnectRef.current?.();
        });
      } catch {
        console.warn("Error connecting to websocket");
      }
    };

    connectSocket();

    return () => {
      cancelled = true;
      const socket = sessionRef.current;
      if (socket) {
        if (previousChannelRef.current) {
          dmnIdentity
            .getAccessToken()
            .then((token) => {
              socket.emit("unsubscribeFromChannel", {
                channel: previousChannelRef.current,
                token
              });
            })
            .finally(() => {
              socket.close();
            });
        } else {
          socket.close();
        }
        sessionRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!sessionRef.current?.connected) return;

    (async () => {
      await unSubscribe();
      await subscribe();
    })();
  }, [channel, subscribe, unSubscribe]);
};
